/**
 * Mobile App Onboarding Config
 *
 * Pre-login onboarding copy (carousel slides) and login-screen text that the
 * mobile app renders BEFORE authentication. Today this content ships in the
 * repo per school (`apps/mobile/src/assets/schools/<id>/onboarding-config.ts`)
 * and is selected at build time via a static `require()` map in
 * `apps/mobile/src/config/build-config.ts`.
 *
 * This schema is the registry-served source of truth (plan
 * 2026-06-20-002 U2): the content is stored on the `MobileApp` registry row
 * (JSON column `onboardingConfig`, sibling to `brandingConfig`) and served from
 * a PUBLIC endpoint keyed by the build-time `schoolAppId` — read before login,
 * so unauthenticated, same posture as the public asset-manifest. The mobile app
 * (U3) fetches it, caches it on device, and falls back to a generic bundled
 * config when no cache exists.
 *
 * Field shape mirrors exactly what the mobile getters consume in
 * `apps/mobile/src/config/build-config.ts` (`getLoginText`,
 * `getOnboardingSlides`, `getOnboardingText`) and is imported directly by the
 * per-school `onboarding-config.ts` files (they used to redeclare it, once each,
 * and had drifted — plan 2026-07-22-005 U1 collapsed them onto this type).
 *
 * ## This schema is the canonical login slot vocabulary (plan 2026-07-22-005 R16)
 *
 * It is the single, shared set of pre-login content slots that EVERY school
 * fills — logo sizing, headline, subtitle, primary-button label, footer text,
 * footer link, background + overlay, onboarding bypass. No school defines its
 * own content structure; a school customizes by filling these named slots
 * (data, registry-overridable) and, if it needs a different arrangement, by
 * selecting a different *layout* (code, chosen at build time by the
 * `SCHOOL_APP`-keyed metro rule in `apps/mobile/metro.config.js`). Layout is
 * deliberately NOT a field here.
 *
 * **Extension convention — follow it.** A genuinely new pre-login element is
 * one new *optional, typed* field on this object plus the layout(s) that render
 * it. Layouts ignore slots they do not render, so adding a field never touches
 * an existing layout. Do NOT add an open `extras` / `Record<string, unknown>`
 * bag: untyped per-school content is exactly the bespoke-structure problem this
 * vocabulary exists to prevent. Keep the shape FLAT — these fields are already
 * persisted flat on `MobileApp.onboardingConfig` rows, so regrouping them into
 * nested objects would break every stored config.
 */

import { z } from 'zod'

/**
 * A single onboarding carousel slide. Keys are SCREAMING_CASE to match the
 * mobile repo's `OnboardingSlide` interface verbatim (the getters spread these
 * straight into the UI).
 */
export const MobileAppOnboardingSlideSchema = z.object({
  TITLE: z.string().max(200),
  DESCRIPTION: z.string().max(2000)
})
export type MobileAppOnboardingSlide = z.infer<typeof MobileAppOnboardingSlideSchema>

/**
 * The full onboarding config persisted on `MobileApp.onboardingConfig`.
 *
 * Optionality mirrors the mobile repo `OnboardingConfig` interface and the
 * `getLoginText` getter defaults: required fields are the ones the getters read
 * unconditionally; the `?`-typed mobile fields are optional here too (the
 * getters apply `?? <default>` for them).
 */
export const MobileAppOnboardingConfigSchema = z
  .object({
    // Onboarding carousel. Bounds cap the payload the PUBLIC endpoint serves
    // verbatim to every pre-login client. The writers are NOT all trusted: a
    // school admin writes this copy through the school lane
    // (`PUT /v1/schools/:school_id/mobile-app/onboarding-config`), so these caps
    // are the limit on what a school can publish to a signed-out screen, not
    // just a guard against a super-admin mispaste. The existing repo configs sit
    // well within. The school lane cannot write the layout-tuning numbers or the
    // carousel switch at all (`SCHOOL_LOCKED_ONBOARDING_FIELDS`).
    slides: z.array(MobileAppOnboardingSlideSchema).max(10),
    nextButtonText: z.string().max(200),
    getStartedText: z.string().max(200),

    // Login-screen copy (required — getLoginText reads these unconditionally).
    //
    // `welcomeText` and `subtitle` are the two overflow-bearing slots: both are
    // registry-overridable with no rebuild, and both feed the no-clip sheet
    // panel. Their bounds are deliberately tighter than the generic 2000 —
    // a registry mispaste must not be able to push arbitrarily long copy onto a
    // pre-login screen, where there is no rebuild gate to catch it. Every baked
    // per-school value sits well inside these.
    welcomeText: z.string().max(200),
    welcomeTextEnabled: z.boolean(),
    subtitle: z.string().max(500),
    subtitleEnabled: z.boolean(),
    buttonText: z.string().max(200),
    logoWidth: z.number().max(2000),
    logoHeight: z.number().max(2000),
    authenticationFooterText: z.string().max(2000),

    // Optional login-screen tuning (getLoginText applies `?? default`)
    logoMarginTop: z.number().optional(),
    logoAbsolutePosition: z.boolean().optional(),
    buttonMarginBottom: z.number().optional(),
    // Points between the last line of login copy and the first action button.
    // Absent means the layout's own committed value, so no existing school moves.
    //
    // Like `logoMarginTop` (classic-only), this is honored by the layouts that have
    // somewhere to put it — today the `photo` layout, which defaults it to its
    // `COPY_TO_ACTIONS_GAP`. Bounded because it is registry-writable: an unbounded
    // value on a pre-login screen pushes the button off the frame with no rebuild
    // gate to catch it.
    copyToActionsGap: z.number().min(0).max(200).optional(),
    // Headline point size, when the layout's own type scale is wrong for THIS
    // school's copy — a long welcome line wrapping to three lines is the usual
    // reason. Absent means the layout's committed size (`photo` uses `text-4xl`,
    // 36pt), so no existing school changes. Bounded for the same registry-write
    // reason as the gap above.
    welcomeTextFontSize: z.number().min(12).max(72).optional(),
    loginBackgroundEnabled: z.boolean().optional(),
    loginBackgroundOverlayOpacity: z.number().min(0).max(1).optional(),
    authenticationFooterTextEnabled: z.boolean().optional(),

    // Footer link slot — an optional labelled link below the primary button
    // (e.g. an alumni portal). Rendered only when BOTH fields are present.
    // `https:`-only at the schema boundary: a pre-login screen opens this in the
    // system browser, so `http:` and custom schemes are rejected here rather
    // than trusted downstream (the render path re-validates too — a hand-edited
    // repo config and a registry payload arrive by different paths).
    footerLinkText: z.string().max(200).optional(),
    footerLinkUrl: z.string().url().startsWith('https://').optional(),
    // Optional plain-text lead-in rendered BEFORE the link, not underlined, so the
    // row can read "Alumni? Visit <Marksmen Connect>" with only the destination
    // styled as a link. Absent means the whole label is the link, which is what
    // every school did before this existed — so no existing config changes.
    // It is its own typed slot rather than a delimiter inside `footerLinkText`,
    // because a magic separator in user-editable copy is a trap for whoever edits
    // it next through the registry.
    footerLinkPrefix: z.string().max(200).optional(),

    // Pre-login onboarding carousel switch. Resolved `?? true`, so ABSENT means
    // the carousel stays on and no existing school changes behavior.
    //
    // This one only works from the BUNDLED config, not the registry: onboarding
    // shows on FIRST INSTALL, which is exactly the launch where the device cache
    // is empty and the registry value has not arrived (the fetch writes the cache
    // for the NEXT cold start). A registry-only flag would be read one launch too
    // late every time. It lives on this same object so it rides both paths for
    // free.
    //
    // Full precedence — this field vs `brandingConfig.onboardingEnabled`, and the
    // bundled `false` that outranks both one-way — is documented once, on
    // `getOnboardingEnabled()` in `apps/mobile/src/config/build-config.ts`.
    onboardingEnabled: z.boolean().optional()
  })
  .strict()
export type MobileAppOnboardingConfig = z.infer<typeof MobileAppOnboardingConfigSchema>

/**
 * Which write path saved an onboarding config
 * (`MobileAppOnboardingConfigVersion.source`, plan 2026-09-01-0709 U6).
 *
 * THIS FILE IS WHERE THE CLOSED SET LIVES. The column is a `VarChar`, not a
 * database enum, for the reason {@link ./mobile-app-credential-pointer.ts}
 * gives one model over: every added value costs a hand-performed PlanetScale
 * promotion, and this set is expected to grow. It has: the school-facing
 * Design editor's lane (`school_admin`, below) was an edit to this array,
 * where a MySQL enum would have been a human promotion step.
 *
 * THE LANE IS RECORDED BESIDE THE ACTOR, NOT INFERRED FROM IT, and that split
 * is what makes a restore decidable. `actorId` says which principal wrote; only
 * the lane says whether a human sat behind it. An automated backfill and a
 * considered super-admin edit can both carry a principal, and the registry lane
 * has no deploy gate, so an operator choosing which version to roll back to has
 * nothing else to separate the two.
 *
 * LOWER SNAKE_CASE to match the closed unions around it — `never_checked`,
 * `stale_check`, `observed`. Note the near-neighbour that is NOT this token:
 * `system-flag.ts` spells its default caller identity `'internal-api'` with a
 * hyphen. The two are unrelated vocabularies and differ by a separator, so do
 * not pass one where the other is expected.
 *
 * THE MIRROR LANE, DECIDED (plan 2026-09-01-2310 U8 / KTD9). This set used to
 * leave `updatePreLoginSettings` unnamed and hand the choice to whoever wired
 * the snapshot in `crud/onboarding-config.ts`. It is wired, and the answer is a
 * value of its own — `pre_login_settings` — NOT the calling lane. The mirror
 * rewrites the whole blob to keep one field (`onboardingEnabled`) in step with
 * `brandingConfig`, so a version row it displaces holds copy nobody was editing.
 * Riding the caller's lane would have made that row indistinguishable from a
 * considered edit of the copy itself, which is exactly the distinction an
 * operator picking a version to restore is looking at. It fits the 32-character
 * `VarChar` and cost no promotion, which is the whole point of the column not
 * being a MySQL enum.
 *
 * `super_admin` — the audited web editor, driven by a real Clerk operator
 * session; `actorId` is that operator.
 * `internal_api` — the headless `/internal/` PUT authenticated by x-api-key
 * (an agent or an ops caller), with no Clerk principal behind it.
 * `seed_script` — an automated seed or backfill run, including U7's
 * seed-on-create.
 * `pre_login_settings` — the pre-login switch mirror described above. The copy
 * in a row it displaced was not itself being edited.
 * `school_admin` — the school dashboard's Custom App Design tab, through
 * `/v1/schools/:school_id/mobile-app/onboarding-config` (plan 2026-08-12-001
 * KTD2). Chosen BY ROUTE, not derived from the actor: a school admin and a
 * super-admin are both `human`, so the actor cannot tell the lanes apart, and a
 * super-admin editing through the school tab is recorded here under their own
 * actor.
 */
export const ONBOARDING_CONFIG_SOURCES = [
  'super_admin',
  'internal_api',
  'seed_script',
  'pre_login_settings',
  'school_admin'
] as const

export const MobileAppOnboardingConfigSourceSchema = z.enum(ONBOARDING_CONFIG_SOURCES)
export type MobileAppOnboardingConfigSource = z.infer<typeof MobileAppOnboardingConfigSourceSchema>
